import { useCallback, useEffect, useState } from "react";
import { getOrder } from "../api/user";

// The open position the order ticket can close, for the asset on screen.
//
// Client: "Users should not have to leave the Trade screen to check their
// current position before closing it. When a user already holds a position and
// opens that asset on the Trade screen, we should show the relevant position
// information directly inside the Qty menu."
//
// One hook, read by BOTH order tickets — the desktop InlineBuySellPanel and the
// mobile MobileChartToolbar — because the client asked for the same interaction
// on both ("I would use the same interaction on desktop for consistency"), and a
// second copy of this arithmetic is a second chance to disagree about how much a
// user is allowed to close.

// A symbol as the ticket sees it vs. as an order row stores it. Polygon prefixes
// crypto with "X:" and forex with "C:", and the two sides of the app do not
// always carry the prefix, so both are reduced to the bare instrument before
// they are compared. Without this, a crypto position never matches its own chart.
const sameSymbol = (a, b) => {
  const bare = (v) =>
    String(v || "")
      .toUpperCase()
      .replace(/^[XC]:/, "");
  const left = bare(a);
  return !!left && left === bare(b);
};

// Holdings rows are synthesised per symbol by the backend's getSortSellOrder:
// order_type 4 is the net LONG, order_type 3 the net SHORT, and order_quantity
// is what is still open (quantity minus what has been covered). Any other
// order_type on a holdings row is not a position.
const POSITION_DIRECTION = { 3: "short", 4: "long" };

// Which pending order is an intent to close THIS position.
//
// postion_type (the backend's spelling): 1 market, 2 limit, 3 stop.
// order_type: 2 SELL closes a long, 5 COVER closes a short. A pending order to
// OPEN (1 buy / 3 short) closes nothing and is ignored — client: "Opposite
// direction resting orders are adds/averages, not closes. Never touch them."
//
// `reserved` is REPORTED but no longer SUBTRACTED from what the shortcuts offer.
// Client: "Close Half / Close All shortcut quantity = full net position size. It
// ignores any resting reservations." Their worked example is the reason:
// "Long 500 with a resting sell limit 200. Tap Close All. Result: filled 500,
// limit cancelled, position flat. Not 300." The first build offered 300 here.
//
// What makes that safe is the backend, not this file: a closing fill now cancels
// or clamps every resting closing-direction order in the same transaction, so a
// full close can no longer leave a stale order behind to flip the user short.
const CLOSING_ORDER_TYPE = { long: 2, short: 5 };

// How much of the position already has a resting LIMIT order pointed at it.
//
// Computed and returned on the position, but deliberately NOT deducted from
// what the chips fill, and nothing renders it yet.
//
// It is kept because the client asked for the reserve to survive: "The existing
// 'subtract resting limit' reserve logic stays, but scope it to manual quantity
// entry only, not the shortcut." What is NOT settled is what it should DO on
// manual entry, because their own check list rules out the obvious reading —
// "Long 500 with resting sell limit 500. Manually sell 300. Result: hold 200,
// limit clamped to 200" requires that 300 to be ACCEPTED, and a reserve that
// blocked would have refused it (500 held - 500 reserved = 0 allowed).
//
// So manual entry is bounded by the real position and the backend clamps the
// resting order after the fill. This value is here for whatever the reserve
// turns out to mean once that is answered. A stop is excluded either way: the
// backend withdraws a stop by itself once the position is gone.
const reservedByOpenOrders = (openOrders, symbol, direction) =>
  (openOrders || [])
    .filter(
      (o) =>
        sameSymbol(o?.stock_symbol, symbol) &&
        Number(o?.postion_type) === 2 &&
        Number(o?.order_type) === CLOSING_ORDER_TYPE[direction]
    )
    .reduce((sum, o) => sum + (Number(o?.order_quantity) || 0), 0);

/**
 * @param {object}  args
 * @param {string}  args.symbol   the instrument the ticket is showing
 * @param {number}  args.gameId   null on the main account; the league or cash
 *                                game's id inside a competition. Client rule 2:
 *                                "Use the position from the current portfolio
 *                                context, whether that is the main account, a
 *                                league, or a Cash Game." get-order scopes to
 *                                exactly that on the server.
 * @param {boolean} args.enabled
 */
export default function useClosablePosition({ symbol, gameId = null, enabled = true } = {}) {
  const [position, setPosition] = useState(null);

  const refresh = useCallback(async () => {
    if (!symbol) {
      setPosition(null);
      return null;
    }
    try {
      // The HOLDINGS call decides whether there is a position at all, so it is
      // the only one allowed to fail the read. The open-orders call feeds
      // `reserved`, which nothing currently blocks on, so a failure there must
      // not be able to hide a position the user really holds — which is what a
      // bare Promise.all did, since one rejection rejected both.
      const holdingsRes = await getOrder(3, null, gameId);
      const openRes = await getOrder(1, null, gameId).catch((e) => {
        console.error("Open-order read failed; position shown without it:", e);
        return null;
      });

      const holdings = holdingsRes?.data?.data?.data || [];
      const openOrders = openRes?.data?.data?.data || [];

      const row = holdings.find(
        (h) =>
          sameSymbol(h?.stock_symbol, symbol) &&
          POSITION_DIRECTION[Number(h?.order_type)]
      );
      if (!row) {
        setPosition(null);
        return null;
      }

      const direction = POSITION_DIRECTION[Number(row.order_type)];
      const held = Number(row.order_quantity) || 0;
      const reserved = reservedByOpenOrders(openOrders, symbol, direction);

      // Rule 1: "Only show these actions when the user has an open position in
      // the selected asset." `held` is the full net position — what the
      // shortcuts fill and what any close is measured against.
      const next = held > 0 ? { direction, held, reserved } : null;
      setPosition(next);
      return next;
    } catch (e) {
      console.error("Could not read the position for the order ticket:", e);
      // A failed read must not invent a position to close.
      setPosition(null);
      return null;
    }
  }, [symbol, gameId]);

  useEffect(() => {
    if (!enabled) {
      setPosition(null);
      return;
    }
    refresh();
  }, [enabled, refresh]);

  return { position, refreshPosition: refresh };
}

// The two chips, derived from a position.
//
// Client's mockup puts the verb in the caption ("CLOSE POSITION") and leaves the
// chips reading just Half and All, so one wording covers a long and a short
// alike. The DIRECTION each chip selects is still per-position, and the client
// restated it to be confirmed:
//
//   Short position: Half or All selects Buy    (a short closes by buying)
//   Long  position: Half or All selects Sell   (a long closes by selling)
//
// "The user must still tap Place Order" — these only fill the ticket.
//
// HALF, exactly as specified: "for an odd position, Half should use the valid
// whole-unit quantity without exceeding half. For example, Half of 501 shares
// should populate 250 shares. The user can close the remaining 251 shares
// afterward." Math.floor is that rule — it can never exceed half, and it can
// never be a fraction of a unit. On a position of one it would be 0, so the Half
// chip is dropped rather than offered as a no-op.
export const positionCloseActions = (position) => {
  if (!position) return [];
  const { direction, held } = position;
  const tradeType = direction === "short" ? 1 : 2;
  const half = Math.floor(held / 2);

  const actions = [];
  if (half >= 1) {
    actions.push({ key: "half", label: "Half", quantity: half, tradeType });
  }
  actions.push({ key: "all", label: "All", quantity: held, tradeType });
  return actions;
};
